export interface ProtocolDayData {
  day: number;
  phase: number;
  title: string;
  subtitle: string;
  objective: string;
  why_this_matters: string;
  expected_outcome: string;
  estimated_minutes: number;
  difficulty: "Fácil" | "Moderado" | "Desafiante";
  essential_task: string;
  recommended_tasks: string[];
  practical_tip: string;
  reflection_question?: string;
}

export const protocolDays: ProtocolDayData[] = [
  // FASE 1: Reducir Hinchazón y Organizar lo Básico
  {
    day: 1, phase: 1,
    title: "El Poder de la Hidratación",
    subtitle: "Despierta tu sistema",
    objective: "Crear el hábito de rehidratar tu cuerpo inmediatamente al despertar.",
    why_this_matters: "Durante la noche perdemos líquidos. Un intestino deshidratado es un intestino lento y propenso a la hinchazón.",
    expected_outcome: "Mejor digestión desde la primera comida y mayor claridad mental.",
    estimated_minutes: 5, difficulty: "Fácil",
    essential_task: "Beber un vaso grande de agua (300ml+) apenas te levantes.",
    recommended_tasks: ["Llevar una botella de agua contigo todo el día", "Comer una fruta rica en agua (como sandía o melón)"],
    practical_tip: "Deja el vaso de agua en tu mesa de noche para que sea lo primero que veas al despertar.",
    reflection_question: "¿Cómo sentiste tu nivel de energía después de beber agua esta mañana?"
  },
  {
    day: 2, phase: 1,
    title: "Fibra en la Mañana",
    subtitle: "El combustible correcto",
    objective: "Garantizar una buena dosis de fibra en la primera comida del día.",
    why_this_matters: "La fibra por la mañana estabiliza el azúcar en la sangre, evitando picos de hambre, y 'barre' el intestino.",
    expected_outcome: "Saciedad prolongada y tránsito intestinal más regular.",
    estimated_minutes: 10, difficulty: "Fácil",
    essential_task: "Agregar 1 cucharada de chía, lino o avena a tu desayuno.",
    recommended_tasks: ["Mantener la hidratación matutina del Día 1", "Evitar el azúcar refinado en el desayuno"],
    practical_tip: "Si comes huevos, puedes espolvorear las semillas encima; si comes yogur o fruta, mézclalo ahí.",
    reflection_question: "¿Notaste menos hambre a media mañana hoy?"
  },
  {
    day: 3, phase: 1,
    title: "Verde es Vida",
    subtitle: "Volumen con propósito",
    objective: "Asegurar al menos una porción significativa de vegetales en el almuerzo.",
    why_this_matters: "Los vegetales crudos y cocidos alimentan las bacterias buenas del intestino y dan volumen sin pesar.",
    expected_outcome: "Menos pesadez después del almuerzo y más vitaminas circulando.",
    estimated_minutes: 15, difficulty: "Moderado",
    essential_task: "Llenar al menos la mitad de tu plato del almuerzo con vegetales (ensalada o cocidos).",
    recommended_tasks: ["Aliñar la ensalada con aceite de oliva (grasas buenas)", "Beber agua 30 min antes de la comida, no durante"],
    practical_tip: "Compra hojas ya lavadas si no tienes tiempo, o ralla una zanahoria rápido. ¡Lo simple funciona!",
    reflection_question: "¿Qué vegetal podrías incluir fácilmente en tu rutina diaria?"
  },
  {
    day: 4, phase: 1,
    title: "Reemplazo Inteligente",
    subtitle: "Menos paquetes, más cáscaras",
    objective: "Identificar y reemplazar un alimento ultraprocesado de tu día.",
    why_this_matters: "Los ultraprocesados contienen conservantes y azúcares ocultos que inflaman la mucosa intestinal.",
    expected_outcome: "Reducción de la retención de líquidos y de la hinchazón abdominal.",
    estimated_minutes: 5, difficulty: "Moderado",
    essential_task: "Cambiar tu snack industrializado (galletas, barritas) por un alimento natural (fruta + nueces).",
    recommended_tasks: ["Mantener la regla de la mitad del plato verde en el almuerzo", "Beber té de menta o hinojo después de cenar"],
    practical_tip: "Si te da un antojo dulce, una manzana asada con canela engaña muy bien al cerebro.",
    reflection_question: "¿Cuál fue el ultraprocesado más difícil de evitar hoy?"
  },
  {
    day: 5, phase: 1,
    title: "Movimiento Digestivo",
    subtitle: "Activa tu centro",
    objective: "Mover el cuerpo para estimular físicamente el tránsito intestinal.",
    why_this_matters: "El sedentarismo ralentiza el intestino. Caminar o estirar masajea los órganos internos mecánicamente.",
    expected_outcome: "Reducción de gases atrapados y sensación de ligereza.",
    estimated_minutes: 15, difficulty: "Fácil",
    essential_task: "Hacer una caminata de 15 minutos o una sesión de estiramiento suave.",
    recommended_tasks: ["No sentarte inmediatamente después de almorzar", "Tomar 5 respiraciones profundas llevando el aire al abdomen"],
    practical_tip: "Aprovecha para escuchar un podcast o tu música favorita durante estos 15 minutos.",
    reflection_question: "¿Sientes el cuerpo menos rígido después de moverte?"
  },
  {
    day: 6, phase: 1,
    title: "Planificación de Snacks",
    subtitle: "No decidas con hambre",
    objective: "Tener listos tus snacks del día para evitar malas decisiones por impulso.",
    why_this_matters: "Cuando el hambre ataca de repente, buscamos energía rápida (azúcares). Planificar previene esto.",
    expected_outcome: "Mantenimiento estable de energía y cero culpa por picar entre comidas.",
    estimated_minutes: 10, difficulty: "Moderado",
    essential_task: "Dejar preparado o comprado tu snack saludable (ej. fruta picada, frutos secos, yogur) desde la mañana.",
    recommended_tasks: ["Beber agua antes de comerte el snack para descartar 'sed disfrazada de hambre'", "Incluir una fuente de proteína o grasa buena en el snack"],
    practical_tip: "Lleva bolsitas zip-lock con almendras en tu bolso de forma permanente.",
    reflection_question: "¿Notaste que es más fácil decir 'no' a la comida chatarra cuando ya tienes algo sano listo?"
  },
  {
    day: 7, phase: 1,
    title: "Consolidación: Semana 1",
    subtitle: "Tu primera base",
    objective: "Reconocer tus victorias y reflexionar sobre cómo se siente tu cuerpo sin la hinchazón constante.",
    why_this_matters: "Celebrar las pequeñas victorias reprograma tu cerebro para querer seguir cuidándote.",
    expected_outcome: "Motivación renovada y claridad mental sobre qué hábito te hizo mejor.",
    estimated_minutes: 5, difficulty: "Fácil",
    essential_task: "Escribir (o pensar) en 1 cambio positivo que sentiste esta semana.",
    recommended_tasks: ["Mantener el vaso de agua en ayunas", "Planificar mentalmente qué comerás mañana"],
    practical_tip: "No busques perfección. Si lograste cumplir 3 de los 7 días bien, ya es una victoria. Sigue adelante.",
    reflection_question: "¿Qué hábito de esta semana quieres que te acompañe para siempre?"
  },

  // FASE 2: Mejorar Ritmo Alimentario y Regularidad
  {
    day: 8, phase: 2,
    title: "Magia Fermentada",
    subtitle: "Vida para tus bacterias",
    objective: "Introducir un alimento probiótico o fermentado natural.",
    why_this_matters: "Tu microbiota necesita 'refuerzos' vivos para luchar contra la inflamación y absorber mejor los nutrientes.",
    expected_outcome: "Mejora progresiva en la digestión de lácteos y otros alimentos.",
    estimated_minutes: 5, difficulty: "Moderado",
    essential_task: "Consumir una porción de yogur natural, kéfir, chucrut o kombucha.",
    recommended_tasks: ["Revisar que el yogur no tenga azúcar añadida (solo leche y fermentos)", "Mantener el agua matutina"],
    practical_tip: "Un poco de yogur griego con miel y frutos rojos es un excelente snack fermentado.",
    reflection_question: "¿Ya habías incluido probióticos naturales antes?"
  },
  {
    day: 9, phase: 2,
    title: "Masticación Consciente",
    subtitle: "La digestión empieza en la boca",
    objective: "Comer al menos una comida del día prestando atención exclusiva a masticar.",
    why_this_matters: "El estómago no tiene dientes. Masticar mal sobrecarga el intestino y genera muchísimos gases.",
    expected_outcome: "Menos reflujo, cero pesadez inmediata post-comida y mayor saciedad.",
    estimated_minutes: 20, difficulty: "Desafiante",
    essential_task: "Masticar al menos 20 veces cada bocado durante tu almuerzo o cena.",
    recommended_tasks: ["Soltar los cubiertos en la mesa entre bocado y bocado", "Comer sin el teléfono cerca"],
    practical_tip: "Concéntrate en la textura de la comida. Hasta que no sea casi líquida, no la tragues.",
    reflection_question: "¿Sentiste que te llenaste más rápido al comer más lento?"
  },
  {
    day: 10, phase: 2,
    title: "Balance Perfecto",
    subtitle: "Proteína, fibra y grasa",
    objective: "Asegurar que tu comida principal tenga los 3 macronutrientes esenciales.",
    why_this_matters: "Comer solo carbohidratos fermenta rápido y da hambre rápido. El balance da saciedad sostenida.",
    expected_outcome: "Energía estable por la tarde, sin ese cansancio o sueño post-almuerzo.",
    estimated_minutes: 15, difficulty: "Moderado",
    essential_task: "Verificar que tu almuerzo tenga 1 proteína, 1 fibra (vegetales/granos) y 1 grasa buena.",
    recommended_tasks: ["Usar aceite de oliva extra virgen en crudo", "Agregar semillas de calabaza o girasol a la ensalada"],
    practical_tip: "Imagina tu plato como un símbolo de la paz: dividido en 3. Medio plato de verdes, un cuarto de carbos, un cuarto de proteína.",
    reflection_question: "¿Experimentaste la 'caída de energía' de las 3 PM hoy?"
  },
  {
    day: 11, phase: 2,
    title: "Ritmo y Horarios",
    subtitle: "A tu intestino le encanta la rutina",
    objective: "Hacer tus 3 comidas principales más o menos a la misma hora de siempre.",
    why_this_matters: "El sistema digestivo funciona con un reloj biológico (ritmo circadiano). Horarios fijos facilitan la ida al baño.",
    expected_outcome: "Mejor sincronización del tránsito intestinal (ir al baño a la misma hora).",
    estimated_minutes: 5, difficulty: "Moderado",
    essential_task: "Fijar un horario para Desayuno, Almuerzo y Cena hoy, y respetarlo con un margen de 30 min.",
    recommended_tasks: ["Evitar picar sin parar entre comidas", "Cenar al menos 2-3 horas antes de dormir"],
    practical_tip: "Si tu agenda es un caos, pon alarmas en el teléfono para recordarte almorzar.",
    reflection_question: "¿Sueles comer en horarios muy desordenados?"
  },
  {
    day: 12, phase: 2,
    title: "Detectando el Azúcar",
    subtitle: "Leyendo entre líneas",
    objective: "Tomar conciencia de dónde se esconde el azúcar añadido en lo que consumes.",
    why_this_matters: "El exceso de azúcar alimenta bacterias patógenas que causan inflamación severa e hinchazón dura.",
    expected_outcome: "Sensación de paladar más limpio y menor deseo impulsivo de dulces.",
    estimated_minutes: 10, difficulty: "Desafiante",
    essential_task: "No agregar azúcar ni edulcorante a tus bebidas (café, té) por un día entero.",
    recommended_tasks: ["Leer los ingredientes del pan o salsa que tengas en casa (suele haber azúcar oculta)", "Endulzar con canela o extracto de vainilla real"],
    practical_tip: "Al principio sabe raro, pero tu paladar se adapta en solo 3 días a saborear el alimento real.",
    reflection_question: "¿Cuántas cucharadas de azúcar 'automáticas' evitaste hoy?"
  },
  {
    day: 13, phase: 2,
    title: "Arcoíris en el Plato",
    subtitle: "Diversidad microbiana",
    objective: "Consumir al menos 3 colores diferentes de vegetales y frutas en un solo día.",
    why_this_matters: "Diferentes colores aportan diferentes polifenoles, que alimentan distintas cepas de bacterias buenas.",
    expected_outcome: "Un intestino mucho más resiliente frente a comidas pesadas.",
    estimated_minutes: 15, difficulty: "Moderado",
    essential_task: "Armar un plato que tenga al menos 3 colores naturales (ej: tomate rojo, lechuga verde, zanahoria naranja).",
    recommended_tasks: ["Variar el color de las frutas que comes (una amarilla, una roja)", "Hacer una cena colorida"],
    practical_tip: "El morado (remolacha, arándanos, cebolla morada) es excelente para reducir la inflamación.",
    reflection_question: "¿Cuáles fueron tus colores de hoy?"
  },
  {
    day: 14, phase: 2,
    title: "Consolidación: Semana 2",
    subtitle: "La consistencia da frutos",
    objective: "Reflexionar sobre la mejoría en tu ritmo y síntomas desde el día 1.",
    why_this_matters: "2 semanas es el tiempo que tarda la microbiota en empezar a mostrar un cambio real de composición.",
    expected_outcome: "Confianza en el proceso y un abdomen visiblemente más desinflamado por la mañana.",
    estimated_minutes: 10, difficulty: "Fácil",
    essential_task: "Hacer una lista mental o escrita de 2 síntomas que te molestaban y han mejorado.",
    recommended_tasks: ["Darte un capricho no alimentario (un buen baño, un episodio de tu serie favorita)", "Agradecer a tu cuerpo por responder bien"],
    practical_tip: "Tómate una foto o mírate al espejo y nota cómo tu postura y abdomen han cambiado.",
    reflection_question: "¿Te sientes con más control sobre lo que comes que hace 14 días?"
  },

  // FASE 3: Consolidar y Sostener Hábitos
  {
    day: 15, phase: 3,
    title: "La Rutina Nocturna",
    subtitle: "El intestino limpia de noche",
    objective: "Preparar el ambiente para que tu sistema digestivo descanse y se repare.",
    why_this_matters: "El 'complejo motor migratorio' (las ondas que limpian el intestino) solo funciona bien si estás en ayuno y descansando profundamente.",
    expected_outcome: "Amanecer con el estómago plano y sin sensación de pesadez del día anterior.",
    estimated_minutes: 10, difficulty: "Moderado",
    essential_task: "Cenar ligero y dejar pasar al menos 2.5 horas antes de acostarte a dormir.",
    recommended_tasks: ["Tomar un té de manzanilla o toronjil 1 hora antes de dormir", "Alejar el teléfono de la cama"],
    practical_tip: "Si cenas tarde por trabajo, haz que el almuerzo sea tu comida principal y la cena algo muy ligero como una sopa o huevos.",
    reflection_question: "¿Dormiste más profundamente al no irte a la cama lleno?"
  },
  {
    day: 16, phase: 3,
    title: "Ambiente Seguro",
    subtitle: "Ajusta tu entorno",
    objective: "Modificar algo en tu cocina o lugar de trabajo para que el buen hábito sea automático.",
    why_this_matters: "La fuerza de voluntad se agota. Si el entorno ayuda, no necesitas pensar para comer bien.",
    expected_outcome: "Reducción dramática del esfuerzo necesario para mantener el protocolo.",
    estimated_minutes: 15, difficulty: "Fácil",
    essential_task: "Colocar un bowl con fruta visible en la cocina o tu escritorio, y guardar los dulces en un cajón cerrado.",
    recommended_tasks: ["Tener una botella de agua siempre a la vista en tu lugar de trabajo", "Lavar y picar vegetales y dejarlos en tapers transparentes en la nevera"],
    practical_tip: "Lo que el ojo no ve, la mente no desea. Oculta las tentaciones, exhibe lo saludable.",
    reflection_question: "¿Notaste que picoteaste la fruta solo porque estaba ahí visible?"
  },
  {
    day: 17, phase: 3,
    title: "Cero Culpa",
    subtitle: "Cómo manejar un 'desliz'",
    objective: "Aprender a volver al foco rápidamente sin castigarte si comiste de más.",
    why_this_matters: "El estrés por haber comido mal daña más el intestino que la comida en sí. La culpa paraliza; la acción repara.",
    expected_outcome: "Paz mental y rápida recuperación digestiva tras un día libre.",
    estimated_minutes: 5, difficulty: "Fácil",
    essential_task: "Si comiste pesado, en tu siguiente comida no restrinjas: simplemente haz tu plato perfecto (proteína, verduras, agua).",
    recommended_tasks: ["Tomar un té digestivo si te sientes hinchado", "Dar un paseo después de la comida pesada"],
    practical_tip: "No intentes 'compensar' saltándote comidas. Vuelve a la rutina normal; tu cuerpo sabe qué hacer.",
    reflection_question: "¿Eres muy duro/a contigo mismo/a cuando te sales del plan?"
  },
  {
    day: 18, phase: 3,
    title: "Estrategia Anti-Recaída",
    subtitle: "Tus mínimos innegociables",
    objective: "Definir tus 3 hábitos base que vas a mantener pase lo que pase.",
    why_this_matters: "En semanas de alto estrés, no podrás hacerlo todo perfecto. Tener 'mínimos' te mantiene a flote.",
    expected_outcome: "Construir resiliencia para que un mes malo no destruya tu intestino otra vez.",
    estimated_minutes: 10, difficulty: "Moderado",
    essential_task: "Anotar cuáles son los 3 hábitos del protocolo que te hacen sentir mejor y comprometerte a no dejarlos.",
    recommended_tasks: ["Compartir tus 3 innegociables con alguien de confianza", "Repasar mentalmente cómo te sentías en el Día 1"],
    practical_tip: "Ejemplo de mínimos: 1. Beber agua al despertar. 2. Verdura en almuerzo. 3. Moverme 15 mins.",
    reflection_question: "¿Cuáles son tus 3 mínimos innegociables?"
  },
  {
    day: 19, phase: 3,
    title: "Inteligencia Emocional",
    subtitle: "Hambre física vs Emocional",
    objective: "Identificar si el hambre que sientes viene del estómago o de la mente.",
    why_this_matters: "El intestino (segundo cerebro) responde al estrés generando 'antojos'. Reconocerlo es el primer paso para no ceder.",
    expected_outcome: "Mayor consciencia antes de comer y reducción de atracones.",
    estimated_minutes: 5, difficulty: "Desafiante",
    essential_task: "Antes de comer un snack hoy, haz una pausa de 10 segundos y pregúntate: ¿Tengo hambre real o estoy estresado/aburrido?",
    recommended_tasks: ["Si es hambre emocional, bebe un vaso de agua y espera 5 minutos", "Si es emocional, intenta hacer otra actividad (leer, caminar, llamar a alguien)"],
    practical_tip: "El hambre física acepta una manzana. El hambre emocional exige chocolate específicamente.",
    reflection_question: "¿Lograste identificar una emoción detrás de un antojo hoy?"
  },
  {
    day: 20, phase: 3,
    title: "Preparación Sostenible",
    subtitle: "El futuro es práctico",
    objective: "Aprender el concepto de 'Meal Prep' (preparación de comidas) básico.",
    why_this_matters: "El principal enemigo de la buena alimentación es la falta de tiempo. Adelantar pasos salva tu digestión.",
    expected_outcome: "Menos estrés diario al cocinar y garantía de comer bien aunque estés cansado.",
    estimated_minutes: 30, difficulty: "Desafiante",
    essential_task: "Cocinar hoy una porción doble de cena para tener el almuerzo de mañana listo.",
    recommended_tasks: ["Cocinar arroz, quinoa o frijoles para 3 días y guardarlo hermético", "Dejar 3 frutas lavadas y listas en la nevera"],
    practical_tip: "Haz siempre más cantidad de la que vas a comer. Es el mismo esfuerzo ensuciar sartenes por 1 porción que por 4.",
    reflection_question: "¿Te dio tranquilidad saber que tu próxima comida ya está lista?"
  },
  {
    day: 21, phase: 3,
    title: "Día 21 — ¡Tu Nuevo Intestino!",
    subtitle: "El final es solo el comienzo",
    objective: "Celebrar intensamente la finalización del protocolo y observar la transformación total.",
    why_this_matters: "Construiste durante 21 días una base sólida para que tu cuerpo sane. Tu intestino ahora está de tu lado.",
    expected_outcome: "Orgullo profundo, abdomen desinflamado, ligereza y energía revitalizada.",
    estimated_minutes: 15, difficulty: "Fácil",
    essential_task: "Mirarte al espejo, agradecer a tu cuerpo por su resiliencia y planear cómo celebrar este logro (sin culpas).",
    recommended_tasks: ["Dejar un post inspirador en la Comunidad contando tu experiencia", "Mantener para mañana los hábitos que más te gustaron"],
    practical_tip: "Tú no terminaste una 'dieta'. Tú aprendiste a vivir mejor. Mantenlo simple y sostenible. 🍃",
    reflection_question: "¿Qué mensaje le darías al 'Tú' que estaba empezando el Día 1?"
  }
];

export interface RecipeData {
  id: string;
  title: string;
  category: string;
  description: string;
  prepTime: string;
  benefitTag: string;
  benefitTags: string[];
  contextTag: string;
  whyItHelps: string;
  usageContext: string;
  ingredients: string[];
  preparation: string;
  substitutions: string[];
  servings: number;
}

export const recipes: RecipeData[] = [
  {
    id: "1", title: "Overnight Oats con Plátano y Chía", category: "cafe",
    description: "Avena preparada la noche anterior, cremosa y rica en fibra.",
    prepTime: "5 min + reposo", benefitTag: "Rica en fibra",
    benefitTags: ["fibra", "saciedad", "facil"], contextTag: "Para mañanas estructuradas",
    whyItHelps: "La fibra soluble de la avena y chía alimenta tus bacterias buenas y regula el tránsito.",
    usageContext: "Ideal para empezar el día con energía sostenida",
    ingredients: ["3 cda. de avena", "1 cda. de chía", "150ml de leche vegetal", "1 plátano", "Canela al gusto"],
    preparation: "Mezcla avena, chía y leche en un frasco. Tapa y refrigera por 6h. Por la mañana, agrega plátano en rodajas y canela.",
    substitutions: ["Leche vegetal → leche descremada", "Plátano → mango o fresas", "Chía → linaza molida"],
    servings: 1
  },
  {
    id: "2", title: "Smoothie Verde Digestivo", category: "bebidas",
    description: "Bebida refrescante con hojas verdes y frutas para empezar bien el día.",
    prepTime: "5 min", benefitTag: "Ligero para el intestino",
    benefitTags: ["ligero", "hinchazon", "rapido"], contextTag: "Ideal para días de hinchazón",
    whyItHelps: "Los vegetales verdes y el pepino son antiinflamatorios naturales que calman el sistema digestivo.",
    usageContext: "Perfecto cuando sientes pesadez o hinchazón",
    ingredients: ["1 hoja de kale", "1/2 pepino", "1 manzana verde", "Jugo de 1 limón", "200ml de agua de coco"],
    preparation: "Licúa todos los ingredientes. Sirve frío.",
    substitutions: ["Kale → espinaca", "Agua de coco → agua natural", "Manzana verde → pera"],
    servings: 1
  },
  {
    id: "3", title: "Bowl de Frutas con Granola Casera", category: "cafe",
    description: "Combinación colorida y nutritiva para un desayuno energético.",
    prepTime: "10 min", benefitTag: "Ayuda a la saciedad",
    benefitTags: ["saciedad", "energia", "fermentado"], contextTag: "Para mañanas con poca energía",
    whyItHelps: "El yogur aporta probióticos y la granola da energía sostenida sin picos de azúcar.",
    usageContext: "Ideal cuando necesitas un desayuno completo y rápido",
    ingredients: ["1 plátano", "5 fresas", "2 cda. de granola sin azúcar", "1 cda. de miel", "Yogur natural"],
    preparation: "Arma el bowl con yogur de base. Agrega frutas en rodajas y granola encima. Finaliza con miel.",
    substitutions: ["Granola → avena tostada", "Miel → stevia", "Yogur → kéfir"],
    servings: 1
  },
  {
    id: "4", title: "Ensalada Colorida con Garbanzos", category: "almoco",
    description: "Ensalada completa, rica en proteína vegetal y fibra.",
    prepTime: "15 min", benefitTag: "Rica en fibra",
    benefitTags: ["fibra", "ligero", "saciedad"], contextTag: "Almuerzo equilibrado y liviano",
    whyItHelps: "Los garbanzos aportan fibra y proteína vegetal que mantienen tu digestión activa.",
    usageContext: "Ideal para un almuerzo liviano que no te deje pesado",
    ingredients: ["1 taza de garbanzos cocidos", "Tomates cherry", "Pepino", "Hojas verdes", "Aceite de oliva y limón"],
    preparation: "Mezcla todos los ingredientes. Adereza con aceite de oliva, limón, sal y hierbas frescas.",
    substitutions: ["Garbanzos → lentejas", "Tomates cherry → pimiento", "Pepino → apio"],
    servings: 2
  },
  {
    id: "5", title: "Pollo a la Plancha con Verduras Asadas", category: "almoco",
    description: "Plato equilibrado, ligero y satisfactorio.",
    prepTime: "30 min", benefitTag: "Ayuda a la saciedad",
    benefitTags: ["saciedad", "energia"], contextTag: "Comida completa y balanceada",
    whyItHelps: "La proteína del pollo combinada con vegetales te da saciedad prolongada sin inflamar.",
    usageContext: "Perfecto para un almuerzo que te dé energía toda la tarde",
    ingredients: ["1 filete de pollo", "Calabacín", "Zanahoria", "Brócoli", "Aceite de oliva y especias"],
    preparation: "Sazona el pollo y ásalo a la plancha. Corta las verduras, rocía con aceite y hornea a 200°C por 20 min.",
    substitutions: ["Pollo → pavo o tofu", "Brócoli → coliflor", "Calabacín → berenjena"],
    servings: 2
  },
  {
    id: "6", title: "Sopa de Calabaza con Jengibre", category: "jantar",
    description: "Sopa caliente y reconfortante, ideal para la cena.",
    prepTime: "25 min", benefitTag: "Ligera para el intestino",
    benefitTags: ["ligero", "hinchazon", "facil"], contextTag: "Cena suave para descansar mejor",
    whyItHelps: "El jengibre es antiinflamatorio natural y la calabaza es suave para el sistema digestivo.",
    usageContext: "Ideal para cenar ligero y despertar sin hinchazón",
    ingredients: ["300g de calabaza", "1 trozo de jengibre", "1 cebolla", "Caldo de verduras", "Sal y nuez moscada"],
    preparation: "Sofríe cebolla y jengibre. Agrega calabaza y caldo. Cocina hasta ablandar y licúa.",
    substitutions: ["Calabaza → zanahoria", "Jengibre → cúrcuma", "Caldo → agua con sal"],
    servings: 2
  },
  {
    id: "7", title: "Wrap de Atún con Ensalada", category: "almoco",
    description: "Práctico y rápido para días ajetreados.",
    prepTime: "10 min", benefitTag: "Práctico",
    benefitTags: ["rapido", "saciedad", "facil"], contextTag: "Para días sin tiempo",
    whyItHelps: "Proteína magra + vegetales en un formato fácil de transportar y comer en cualquier lugar.",
    usageContext: "Perfecto cuando tienes poco tiempo pero quieres comer bien",
    ingredients: ["1 tortilla integral", "1 lata de atún", "Lechuga", "Tomate", "Mostaza"],
    preparation: "Mezcla atún con mostaza. Arma el wrap con lechuga, tomate y atún. Enrolla y sirve.",
    substitutions: ["Atún → pollo desmenuzado", "Tortilla integral → hoja de lechuga grande", "Mostaza → hummus"],
    servings: 1
  },
  {
    id: "8", title: "Té de Menta con Limón", category: "bebidas",
    description: "Té digestivo natural, ideal después de las comidas.",
    prepTime: "5 min", benefitTag: "Ligero para el intestino",
    benefitTags: ["ligero", "hinchazon", "rapido"], contextTag: "Alivia hinchazón post-comida",
    whyItHelps: "La menta relaja los músculos del tracto digestivo, aliviando gases e hinchazón.",
    usageContext: "Tómalo después de comer si sientes pesadez",
    ingredients: ["Hojas de menta fresca", "Jugo de medio limón", "Agua caliente", "Miel (opcional)"],
    preparation: "Coloca las hojas en una taza, vierte agua caliente. Espera 5 min, agrega limón y miel.",
    substitutions: ["Menta → manzanilla", "Miel → stevia", "Limón → naranja"],
    servings: 1
  },
  {
    id: "9", title: "Plátano con Mantequilla de Maní", category: "lanches",
    description: "Snack rápido, nutritivo y delicioso.",
    prepTime: "2 min", benefitTag: "Ayuda a la saciedad",
    benefitTags: ["saciedad", "energia", "rapido", "facil"], contextTag: "Snack rápido y satisfactorio",
    whyItHelps: "La combinación de potasio, grasas buenas y carbohidrato natural da energía rápida y sostenida.",
    usageContext: "Ideal como snack de media mañana o pre-ejercicio",
    ingredients: ["1 plátano", "1 cda. de mantequilla de maní", "Canela (opcional)"],
    preparation: "Corta el plátano en rodajas, agrega mantequilla de maní encima y espolvorea canela.",
    substitutions: ["Mantequilla de maní → mantequilla de almendras", "Plátano → manzana en rodajas"],
    servings: 1
  },
  {
    id: "10", title: "Omelette de Espinaca", category: "cafe",
    description: "Desayuno proteico y ligero.",
    prepTime: "10 min", benefitTag: "Práctico",
    benefitTags: ["rapido", "saciedad", "ligero", "facil"], contextTag: "Desayuno ligero y nutritivo",
    whyItHelps: "La proteína del huevo da saciedad prolongada y la espinaca aporta hierro y fibra.",
    usageContext: "Perfecto para un desayuno rápido que te mantenga sin hambre",
    ingredients: ["2 huevos", "1 puñado de espinaca", "Sal y pimienta", "Aceite de oliva"],
    preparation: "Bate los huevos, agrega espinaca picada. Vierte en el sartén con aceite y cocina de ambos lados.",
    substitutions: ["Espinaca → champiñones", "Huevos → claras de huevo", "Aceite de oliva → mantequilla"],
    servings: 1
  },
  {
    id: "11", title: "Arroz Integral con Lentejas", category: "almoco",
    description: "Combinación clásica, rica en fibra y proteínas.",
    prepTime: "35 min", benefitTag: "Rica en fibra",
    benefitTags: ["fibra", "saciedad", "energia"], contextTag: "Almuerzo completo y nutritivo",
    whyItHelps: "Las lentejas son ricas en fibra prebiótica que alimenta la microbiota intestinal.",
    usageContext: "Ideal para un almuerzo sustancioso que apoya la digestión",
    ingredients: ["1 taza de arroz integral", "1/2 taza de lentejas", "Cebolla", "Ajo", "Sal y laurel"],
    preparation: "Cocina lentejas y arroz por separado. Sofríe cebolla y ajo, mezcla todo.",
    substitutions: ["Arroz integral → quinoa", "Lentejas → frijoles negros"],
    servings: 2
  },
  {
    id: "12", title: "Yogur con Frutos Rojos", category: "lanches",
    description: "Snack probiótico y antioxidante.",
    prepTime: "3 min", benefitTag: "Ligero para el intestino",
    benefitTags: ["fermentado", "ligero", "rapido", "facil"], contextTag: "Snack probiótico diario",
    whyItHelps: "El yogur natural aporta bacterias buenas que fortalecen tu flora intestinal.",
    usageContext: "Ideal como snack cuando el protocolo sugiere alimentos fermentados",
    ingredients: ["1 vaso de yogur natural", "Fresas", "Arándanos", "1 cda. de miel"],
    preparation: "Coloca el yogur en un bowl, agrega frutas y miel.",
    substitutions: ["Yogur → kéfir", "Fresas → mango", "Miel → stevia"],
    servings: 1
  },
  {
    id: "13", title: "Salmón con Batata", category: "jantar",
    description: "Cena nutritiva con grasas buenas y carbohidrato de calidad.",
    prepTime: "30 min", benefitTag: "Ayuda a la saciedad",
    benefitTags: ["saciedad", "energia"], contextTag: "Cena nutritiva y balanceada",
    whyItHelps: "El omega-3 del salmón es antiinflamatorio y la batata aporta energía de liberación lenta.",
    usageContext: "Perfecto para una cena completa sin sentirte pesado después",
    ingredients: ["1 filete de salmón", "1 batata", "Brócoli", "Aceite de oliva", "Limón"],
    preparation: "Hornea la batata. Asa el salmón con limón. Cocina el brócoli al vapor.",
    substitutions: ["Salmón → merluza", "Batata → calabaza", "Brócoli → espárragos"],
    servings: 2
  },
  {
    id: "14", title: "Agua Saborizada de Pepino y Limón", category: "bebidas",
    description: "Hidratación con sabor, sin azúcar añadida.",
    prepTime: "3 min", benefitTag: "Ligera para el intestino",
    benefitTags: ["ligero", "hinchazon", "rapido", "facil"], contextTag: "Hidratación con beneficio digestivo",
    whyItHelps: "Te ayuda a beber más agua de forma natural, clave para un intestino saludable.",
    usageContext: "Ideal para mantener la hidratación durante todo el día",
    ingredients: ["1 litro de agua", "1/2 pepino en rodajas", "1 limón en rodajas", "Hojas de menta"],
    preparation: "Coloca todo en una jarra con agua fría. Deja reposar 30 min en la nevera.",
    substitutions: ["Pepino → fresas", "Limón → naranja", "Menta → albahaca"],
    servings: 4
  },
  {
    id: "15", title: "Panqueque de Plátano y Avena", category: "cafe",
    description: "Panqueque saludable sin harina refinada.",
    prepTime: "15 min", benefitTag: "Rico en fibra",
    benefitTags: ["fibra", "saciedad", "energia"], contextTag: "Desayuno sustancioso y reconfortante",
    whyItHelps: "La avena aporta fibra beta-glucana que favorece el tránsito intestinal.",
    usageContext: "Ideal para un desayuno de fin de semana o cuando quieres algo especial",
    ingredients: ["1 plátano", "2 huevos", "3 cda. de avena", "Canela", "Aceite de coco"],
    preparation: "Aplasta el plátano, mezcla huevos y avena. Vierte porciones en el sartén y dora de ambos lados.",
    substitutions: ["Avena → harina de almendras", "Aceite de coco → mantequilla", "Plátano → puré de manzana"],
    servings: 1
  },
];

export const benefitLabels: Record<string, string> = {
  ligero: "Ligero",
  fibra: "Más fibra",
  saciedad: "Más saciedad",
  rapido: "Rápido",
  hinchazon: "Para hinchazón",
  energia: "Para energía",
  fermentado: "Fermentado",
  facil: "Fácil",
};

export const recipeCollections = [
  { id: "light-breakfast", title: "Desayunos ligeros", emoji: "🌅", recipeIds: ["1", "10", "3"] },
  { id: "more-fiber", title: "Más fibra en tu día", emoji: "🌾", recipeIds: ["1", "4", "11", "15"] },
  { id: "for-bloating", title: "Ideas para hinchazón", emoji: "🍃", recipeIds: ["2", "6", "8", "14"] },
  { id: "quick", title: "Opciones rápidas", emoji: "⚡", recipeIds: ["9", "12", "14", "8"] },
  { id: "satisfying", title: "Snacks satisfactorios", emoji: "💪", recipeIds: ["9", "12", "3"] },
  { id: "gentle-drinks", title: "Bebidas suaves", emoji: "🫖", recipeIds: ["2", "8", "14"] },
];

export const categoryLabels: Record<string, string> = {
  cafe: "Desayuno",
  almoco: "Almuerzo",
  jantar: "Cena",
  lanches: "Snacks",
  bebidas: "Bebidas",
};

export const faqItems = [
  {
    question: "¿Cómo funciona Intestino Leve?",
    answer: "Intestino Leve es una app de bienestar que te guía por un protocolo de 21 días con hábitos simples enfocados en hidratación, fibra, movimiento y consistencia. Haces check-ins diarios, acompañas tu progreso y accedes a recetas saludables."
  },
  {
    question: "¿En cuánto tiempo empiezo a notar diferencia?",
    answer: "Cada persona es única, pero muchos usuarios reportan mejoras en la disposición y regularidad intestinal ya en la primera semana. El protocolo de 21 días fue pensado para crear hábitos que generan resultados sostenibles."
  },
  {
    question: "¿La app sustituye al médico o nutricionista?",
    answer: "No. Intestino Leve es una herramienta de apoyo al bienestar y creación de hábitos. No diagnostica, trata ni previene enfermedades. Para cuestiones de salud, siempre consulta a un profesional calificado."
  },
  {
    question: "¿Necesito seguir una dieta estricta?",
    answer: "¡No! El enfoque está en pequeños cambios y agregar alimentos buenos para el intestino, no en restricción. Incentivamos equilibrio, variedad y placer en la alimentación."
  },
  {
    question: "¿El protocolo es difícil?",
    answer: "El protocolo fue diseñado para ser simple y gradual. Cada día tiene pocas tareas fáciles de encajar en la rutina. No se trata de perfección, se trata de consistencia."
  },
  {
    question: "¿Puedo usarla con una rutina ajetreada?",
    answer: "¡Sí! Las tareas son rápidas y prácticas. El check-in toma menos de 1 minuto. Las recetas incluyen opciones de preparación rápida. La app fue hecha para quien tiene poco tiempo."
  },
];

export const motivationalMessages = [
  "Hoy es un buen día para cuidar tu rutina. 🍃",
  "Pequeños pasos crean grandes cambios. ✨",
  "Tu progreso comienza con consistencia. 💚",
  "Más ligereza en tu día, un hábito a la vez. 🌱",
  "Cuida tu cuerpo con amabilidad. 🤍",
  "Cada check-in es un paso en la dirección correcta. 📝",
  "La hidratación es el primer paso hacia el bienestar. 💧",
];